import { Injectable } from '@angular/core';
import { select, Store, Action } from '@ngrx/store';

import * as fromConsultation from './consultation.reducer';
import * as ConsultationSelectors from './consultation.selectors';
import * as ConsultationActions from './consultation.actions';
import { ConsultationEntity } from './consultation.models';
import {
  NewConsultationDTO,
  UpdateConsultationDTO,
  LoadType
} from '@care-portals/api-interfaces';
import { APIService } from '../../services/api/api.service';
import { firstValueFrom, map } from 'rxjs';
import { NzNotificationService } from 'ng-zorro-antd/notification';

@Injectable()
export class ConsultationFacade {
  loading$ = this.store.pipe(
    select(ConsultationSelectors.getConsultationLoading)
  );
  creating$ = this.store.pipe(
    select(ConsultationSelectors.getConsultationCreating)
  );
  consultations$ = this.store.pipe(
    select(ConsultationSelectors.getConsultationEntities)
  );
  selectedConsultation$ = this.store.pipe(
    select(ConsultationSelectors.getSelected)
  );

  constructor(
    private apiService: APIService,
    private store: Store<fromConsultation.ConsultationPartialState>,
    private notification: NzNotificationService
  ) {}

  dispatch(action: Action) {
    this.store.dispatch(action);
  }

  loadConsultations(loadType: LoadType, filters: any, reset: boolean = false) {
    if (reset) {
      this.dispatch(ConsultationActions.resetConsultations());
    }
    this.dispatch(ConsultationActions.loadConsultations({ loadType, filters }));
  }

  async getLatestConsultation(customerId: string, consult: string = '') {
    return await firstValueFrom(
      this.apiService.getLatestConsultation(customerId, consult)
    ).catch(error => {
      console.error('Error', error);
      this.notification.error(
        'Error',
        'There was an error loading latest consultation.'
      );
      return error;
    });
  }

  async getSatisfiedConsultations(customerId: string) {
    return await firstValueFrom(
      this.apiService.getSatisfiedConsultations(customerId)
    ).catch(error => {
      console.error('Error', error);
      this.notification.error(
        'Error',
        'There was an error loading satisfied consultations.'
      );
      return error;
    });
  }

  async saveConsultation(consultation: NewConsultationDTO) {
    this.dispatch(ConsultationActions.saveConsultation({ consultation }));
    return await firstValueFrom(
      this.apiService
        .saveConsultation(consultation)
        .pipe(
          map(consultation =>
            this.dispatch(
              ConsultationActions.saveConsultationSuccess({ consultation })
            )
          )
        )
    ).catch(error => {
      this.dispatch(ConsultationActions.saveConsultationFailure({ error }));
      return error;
    });
  }

  updateConsultation(
    id: string,
    consultation: UpdateConsultationDTO,
    callback?: () => void
  ) {
    this.dispatch(
      ConsultationActions.updateConsultation({ id, consultation, callback })
    );
  }

  setCurrentConsultation(id: string) {
    this.dispatch(ConsultationActions.setCurrentConsultation({ id }));
  }

  clearNotification(consultation: ConsultationEntity) {
    this.dispatch(ConsultationActions.clearNotification({ consultation }));
  }
}
